import {
  BoxGeometry,
  BufferGeometry,
  Color,
  CylinderGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { createRandom } from '../../../utils/random.js';
import { Prop } from '../../../entities/Prop.js';
import { GROUP, MASK } from '../../../physics/groups.js';
import { createTrafficFlow, carDimensions } from './trafficFlow.js';
import { buildRakshaVisuals } from './raksha.js';
import { buildBusVisuals } from './bus.js';

const GEOMETRY_CACHE = new Map();
const UP = new Vector3(0, 1, 0);

/** Vehicles with a bespoke model. They own their wheels, lamps and passengers,
 *  so the instanced sedan meshes have to leave their slots empty. */
const isCustomVehicle = (record) =>
  record.type === 'military' || record.type === 'raksha' || record.type === 'bus';

/**
 * The long fuel queue is a live simulation: the front cars leave the street
 * line, drive through a forecourt service lane, fill at the pumps and then
 * drive back out onto the northbound carriageway. They continue around a
 * visible U-turn loop and return to the back of their queue rather than being
 * recycled by teleport. Instancing keeps the civilian cars cheap to draw;
 * rakshas and the two military technicals are separate low-poly groups because
 * they carry visible passengers or crew. Every vehicle has a kinematic box body that
 * follows it so the player can still bump into the queue or a returning car.
 */
export function buildTraffic(level, route, materials, stationSetups, hitchhikers) {
  const random = createRandom(4451);
  const flow = createTrafficFlow({
    route,
    stations: stationSetups,
    random,
    hitchhikers,
    rakshaConfig: level.config?.rakshas,
    busConfig: level.config?.buses,
  });
  const { records } = flow;

  const carMeshes = buildCarMeshes(level, records);
  const militaryVisual = buildMilitaryVisuals(level, records);
  const rakshaVisual = buildRakshaVisuals(level, records);
  const busVisual = buildBusVisuals(level, records);
  const visual = createVisualUpdater(records, carMeshes);
  const colliders = createColliderUpdater(level, records);

  addStationCones(level, stationSetups);

  let elapsed = 0;
  const panicSpeed = Math.max(
    1,
    level.config?.droneAttack?.trafficPanicSpeed ?? 1.55,
  );
  const honkPulses = new Map(flow.loops.map((loop) => [loop, 0]));
  const traffic = {
    update: null,
    onQueueRage: null,
    onQueueHonk: null,
    carCount: records.length,
    carMeshes,
    records,
    loops: flow.loops,
    /** The minibus fleet: each entry carries its own circuit curve. */
    buses: flow.buses,
    /** Let the whole street accelerate while an RSF drone is overhead. */
    setPanic(active) {
      flow.panicFactor = active ? panicSpeed : 1;
    },
    get panicFactor() {
      return flow.panicFactor;
    },
  };

  traffic.update = (dt, player) => {
    elapsed += dt;
    const event = flow.update(dt, player);
    if (event) traffic.onQueueRage?.(event);

    for (const loop of flow.loops) {
      if (loop.honkPulse <= (honkPulses.get(loop) ?? 0)) continue;
      honkPulses.set(loop, loop.honkPulse);
      traffic.onQueueHonk?.({
        type: 'queue-honk',
        stationId: loop.station.id,
        stationName: loop.station.nameEnglish,
      });
    }

    const flashRate = flow.panicFactor > 1.001 ? 22 : 8.2;
    const phaseA = Math.sin(elapsed * flashRate) > -0.2;
    const phaseB = Math.sin(elapsed * flashRate + Math.PI) > -0.2;
    carMeshes.hazardMaterials[0].opacity = phaseA ? 0.95 : 0.08;
    carMeshes.hazardMaterials[1].opacity = phaseB ? 0.95 : 0.08;

    // An angry driver flashes the hazards much faster than the ordinary
    // queue, then returns to the normal pattern once the run is over.
    for (const record of records) {
      if (record.angry) {
        record.hazardOn = Math.sin(elapsed * 34 + record.index * 1.7) > 0;
      } else if (record._wasAngry) {
        record.hazardOn = record.state === 'queue' ? record.hazard : false;
      }
      record._wasAngry = Boolean(record.angry);
    }

    visual.update();
    militaryVisual.update();
    rakshaVisual.update(elapsed);
    busVisual.update(elapsed);
    colliders.update(dt);
  };

  return traffic;
}

function buildCarMeshes(level, records) {
  const bodyMaterial = new MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.54,
    metalness: 0.24,
  });
  const glassMaterial = new MeshStandardMaterial({
    color: 0x1c292f,
    roughness: 0.16,
    metalness: 0.45,
    side: DoubleSide,
  });
  const trimMaterial = new MeshStandardMaterial({
    color: 0x2e3231,
    roughness: 0.6,
    metalness: 0.25,
  });
  const tireMaterial = new MeshStandardMaterial({
    color: 0x151617,
    roughness: 0.84,
    metalness: 0.02,
  });

  const wheelGeometry = new CylinderGeometry(0.34, 0.34, 0.24, 10);
  wheelGeometry.rotateZ(Math.PI / 2);
  const wheelMesh = new InstancedMesh(wheelGeometry, tireMaterial, records.length * 4);
  wheelMesh.castShadow = false;
  wheelMesh.frustumCulled = false;

  const lightFrontGeometry = new BoxGeometry(0.28, 0.12, 0.05);
  const lightRearGeometry = new BoxGeometry(0.3, 0.13, 0.05);
  const headlightMaterial = new MeshBasicMaterial({ color: 0xf3e7c8, toneMapped: false });
  const taillightMaterial = new MeshBasicMaterial({ color: 0x9e2820, toneMapped: false });
  const headlights = new InstancedMesh(lightFrontGeometry, headlightMaterial, records.length * 2);
  const taillights = new InstancedMesh(lightRearGeometry, taillightMaterial, records.length * 2);
  headlights.frustumCulled = false;
  taillights.frustumCulled = false;

  const groups = new Map();
  records.forEach((record, index) => {
    if (isCustomVehicle(record)) return;
    if (!groups.has(record.type)) groups.set(record.type, []);
    groups.get(record.type).push({ record, index });
  });

  const byIndex = new Array(records.length);
  const carMatrix = new Matrix4();
  const partMatrix = new Matrix4();
  const zeroMatrix = new Matrix4().makeScale(0, 0, 0);
  const quaternion = new Quaternion();
  const wheelQuaternion = new Quaternion();
  const position = new Vector3();
  const local = new Vector3();
  const unit = new Vector3(1, 1, 1);
  const carColor = new Color();

  for (const [type, items] of groups) {
    const geometry = getCarGeometry(type);
    const groupCount = items.length;
    const bodyMesh = new InstancedMesh(geometry.body, bodyMaterial, groupCount);
    const glassMesh = new InstancedMesh(geometry.glass, glassMaterial, groupCount);
    const trimMesh = new InstancedMesh(geometry.trim, trimMaterial, groupCount);
    bodyMesh.castShadow = true;
    bodyMesh.receiveShadow = true;
    glassMesh.castShadow = true;
    trimMesh.castShadow = true;
    bodyMesh.frustumCulled = false;
    glassMesh.frustumCulled = false;
    trimMesh.frustumCulled = false;

    items.forEach(({ record, index }, groupIndex) => {
      quaternion.setFromAxisAngle(new Vector3(0, 1, 0), record.yaw);
      position.set(record.x, 0.02, record.z);
      carMatrix.compose(position, quaternion, new Vector3(record.scale, 1, record.scale));
      bodyMesh.setMatrixAt(groupIndex, carMatrix);
      glassMesh.setMatrixAt(groupIndex, carMatrix);
      trimMesh.setMatrixAt(groupIndex, carMatrix);
      carColor.setHex(record.color);
      bodyMesh.setColorAt(groupIndex, carColor);

      byIndex[index] = {
        bodyMesh,
        glassMesh,
        trimMesh,
        groupIndex,
        geometry,
      };
    });

    if (bodyMesh.instanceColor) bodyMesh.instanceColor.needsUpdate = true;
    bodyMesh.instanceMatrix.needsUpdate = true;
    glassMesh.instanceMatrix.needsUpdate = true;
    trimMesh.instanceMatrix.needsUpdate = true;
    level.root.add(bodyMesh, glassMesh, trimMesh);
  }

  records.forEach((record, index) => {
    if (isCustomVehicle(record)) {
      // Custom vehicle groups own their wheels and lamps. Collapse the
      // unused instanced slots so they cannot leave a spare set at the origin.
      for (let wheel = 0; wheel < 4; wheel += 1) {
        wheelMesh.setMatrixAt(index * 4 + wheel, zeroMatrix);
      }
      for (let lamp = 0; lamp < 2; lamp += 1) {
        headlights.setMatrixAt(index * 2 + lamp, zeroMatrix);
        taillights.setMatrixAt(index * 2 + lamp, zeroMatrix);
      }
      return;
    }
    const geometry = getCarGeometry(record.type);
    quaternion.setFromAxisAngle(new Vector3(0, 1, 0), record.yaw);
    position.set(record.x, 0.02, record.z);
    carMatrix.compose(position, quaternion, new Vector3(record.scale, 1, record.scale));

    for (let wheel = 0; wheel < 4; wheel += 1) {
      const x = wheel % 2 === 0 ? -0.82 : 0.82;
      const z = wheel < 2 ? -geometry.wheelZ : geometry.wheelZ;
      local.set(x, 0.34, z);
      local.applyMatrix4(carMatrix);
      wheelQuaternion.copy(quaternion);
      partMatrix.compose(local, wheelQuaternion, unit);
      wheelMesh.setMatrixAt(index * 4 + wheel, partMatrix);
    }

    for (let lamp = 0; lamp < 2; lamp += 1) {
      const x = lamp === 0 ? -0.6 : 0.6;
      local.set(x, 0.73, -geometry.lightZ);
      local.applyMatrix4(carMatrix);
      partMatrix.compose(local, quaternion, unit);
      headlights.setMatrixAt(index * 2 + lamp, partMatrix);

      local.set(x, 0.75, geometry.lightZ);
      local.applyMatrix4(carMatrix);
      partMatrix.compose(local, quaternion, unit);
      taillights.setMatrixAt(index * 2 + lamp, partMatrix);
    }
  });

  wheelMesh.instanceMatrix.needsUpdate = true;
  headlights.instanceMatrix.needsUpdate = true;
  taillights.instanceMatrix.needsUpdate = true;
  level.root.add(wheelMesh, headlights, taillights);

  // Hazard lamps: split into two alternating groups so the queue flashes out
  // of phase, like a real line of idling cars.
  const hazardGeometry = new BoxGeometry(0.13, 0.09, 0.05);
  const hazardMaterials = [
    new MeshBasicMaterial({
      color: 0xff9d2a,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      toneMapped: false,
    }),
    new MeshBasicMaterial({
      color: 0xffa42e,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      toneMapped: false,
    }),
  ];
  const hazardRecords = [[], []];
  records.forEach((record, index) => {
    if (!record.hazard) return;
    record.hazardZ = carDimensions(record.type).lightZ;
    hazardRecords[index % 2].push(record);
  });

  hazardRecords.forEach((items, groupIndex) => {
    if (!items.length) return;
    const hazardMesh = new InstancedMesh(hazardGeometry, hazardMaterials[groupIndex], items.length * 4);
    hazardMesh.renderOrder = 3;
    hazardMesh.frustumCulled = false;
    items.forEach((record, itemIndex) => {
      record.hazardMesh = hazardMesh;
      record.hazardBase = itemIndex * 4;
      const carQuat = new Quaternion().setFromAxisAngle(UP, record.yaw);
      const carPos = new Vector3(record.x, 0.02, record.z);
      const matrix = new Matrix4();
      const carTransform = new Matrix4().compose(
        carPos,
        carQuat,
        new Vector3(record.scale, 1, record.scale),
      );
      for (let corner = 0; corner < 4; corner += 1) {
        const x = corner % 2 === 0 ? -0.79 : 0.79;
        const z = corner < 2 ? -record.hazardZ : record.hazardZ;
        const world = new Vector3(x, 0.66, z).applyMatrix4(carTransform);
        matrix.compose(world, carQuat, unit);
        hazardMesh.setMatrixAt(record.hazardBase + corner, matrix);
      }
    });
    hazardMesh.instanceMatrix.needsUpdate = true;
    level.root.add(hazardMesh);
  });

  return { hazardMaterials, byIndex, wheelMesh, headlights, taillights };
}

/**
 * The military technicals are the only traffic vehicles that are not
 * instanced. There are just two of them, so each one gets its own cloned
 * group: an open cab, six individual soldiers, a bed-mounted heavy gun and the
 * blue barrel from the reference pickup.
 */
function buildMilitaryVisuals(level, records) {
  const militaryRecords = records.filter((record) => record.type === 'military');
  if (militaryRecords.length === 0) return { update() {} };

  const template = buildMilitaryTemplate();
  const items = militaryRecords.map((record) => {
    const group = template.clone(true);
    group.name = 'military-technical';
    group.position.set(record.x, record.y ?? 0.02, record.z);
    group.rotation.y = record.yaw;
    group.scale.setScalar(record.scale ?? 1);
    group.visible = record.visible !== false;
    level.root.add(group);
    return { record, group };
  });

  return {
    update() {
      for (const { record, group } of items) {
        const visual = record.visible === false ? 0 : (record.visualScale ?? 1);
        group.visible = visual > 0.02;
        if (!group.visible) continue;
        group.position.set(record.x, record.y ?? 0.02, record.z);
        group.rotation.y = record.yaw;
        const scale = (record.scale ?? 1) * visual;
        group.scale.setScalar(scale);
      }
    },
  };
}

/**
 * The military technical is the only traffic vehicle that is not instanced.
 * There are just two of them, and each one needs an open cab, six individual
 * soldiers, a bed-mounted heavy gun and the blue barrel from the reference
 * pickup. A merged-material template keeps each truck to a handful of draw
 * calls while still allowing the crew to be recognisable at chase distance.
 *
 * The reference truck is a Land-Cruiser-style single cab with its roof cut
 * off, the windscreen folded forward onto the bonnet, two men in the cab and
 * four in the bed around a pedestal machine gun and a blue plastic barrel.
 * The cab is built as an open tub — floor, doors, bulkhead, seats — rather
 * than a solid block, so the two front crew stay visible from the chest up.
 */
function buildMilitaryTemplate() {
  const materials = {
    olive: new MeshStandardMaterial({
      color: 0x6f7752,
      roughness: 0.88,
      metalness: 0.06,
    }),
    oliveLight: new MeshStandardMaterial({
      color: 0x848c66,
      roughness: 0.9,
      metalness: 0.04,
    }),
    oliveDark: new MeshStandardMaterial({
      color: 0x3f4a2f,
      roughness: 0.94,
      metalness: 0.03,
    }),
    sand: new MeshStandardMaterial({
      color: 0x9d8d69,
      roughness: 0.92,
      metalness: 0.02,
    }),
    tire: new MeshStandardMaterial({
      color: 0x151617,
      roughness: 0.86,
      metalness: 0.02,
    }),
    metal: new MeshStandardMaterial({
      color: 0x2a2f2b,
      roughness: 0.54,
      metalness: 0.5,
    }),
    glass: new MeshStandardMaterial({
      color: 0x1d2c2b,
      roughness: 0.18,
      metalness: 0.42,
    }),
    tan: new MeshStandardMaterial({
      color: 0xbca886,
      roughness: 0.9,
      metalness: 0.01,
    }),
    tanDark: new MeshStandardMaterial({
      color: 0x7d6b4e,
      roughness: 0.93,
      metalness: 0.01,
    }),
    maroon: new MeshStandardMaterial({
      color: 0x76232f,
      roughness: 0.86,
      metalness: 0.02,
    }),
    skin: new MeshStandardMaterial({
      color: 0x6b4a34,
      roughness: 0.92,
      metalness: 0,
    }),
    blue: new MeshStandardMaterial({
      color: 0x2b6491,
      roughness: 0.58,
      metalness: 0.28,
    }),
    headlight: new MeshBasicMaterial({ color: 0xffe7a8, toneMapped: false }),
    taillight: new MeshBasicMaterial({ color: 0x9b2b1e, toneMapped: false }),
  };

  const buckets = new Map();
  const add = (material, geometry, position, rotation = null) => {
    if (rotation) {
      if (rotation[0]) geometry.rotateX(rotation[0]);
      if (rotation[1]) geometry.rotateY(rotation[1]);
      if (rotation[2]) geometry.rotateZ(rotation[2]);
    }
    geometry.translate(position[0], position[1], position[2]);
    if (!buckets.has(material)) buckets.set(material, []);
    buckets.get(material).push(geometry);
  };
  const addBox = (material, width, height, depth, x, y, z, rotation = null) => {
    add(material, new BoxGeometry(width, height, depth), [x, y, z], rotation);
  };
  const addCylinder = (
    material,
    radiusTop,
    radiusBottom,
    height,
    segments,
    x,
    y,
    z,
    rotation = null,
  ) => {
    add(
      material,
      new CylinderGeometry(radiusTop, radiusBottom, height, segments),
      [x, y, z],
      rotation,
    );
  };
  const addSphere = (material, radius, x, y, z, scale = null) => {
    const geometry = new SphereGeometry(radius, 10, 8);
    if (scale) geometry.scale(scale[0], scale[1], scale[2]);
    add(material, geometry, [x, y, z]);
  };

  // Big off-road wheels, hub caps and squared arch flares.
  for (const side of [-1, 1]) {
    for (const wheelZ of [-1.49, 1.49]) {
      addCylinder(materials.tire, 0.44, 0.44, 0.32, 14, side * 0.87, 0.44, wheelZ, [0, 0, Math.PI / 2]);
      addCylinder(materials.metal, 0.19, 0.19, 0.33, 12, side * 0.87, 0.44, wheelZ, [0, 0, Math.PI / 2]);
      addCylinder(materials.oliveDark, 0.055, 0.055, 0.36, 6, side * 0.87, 0.44, wheelZ, [0, 0, Math.PI / 2]);
      addBox(materials.oliveDark, 0.1, 0.26, 1.06, side * 0.95, 0.88, wheelZ);
    }
  }

  // Chassis rails and floor pan. The nose is at −Z.
  addBox(materials.metal, 1.5, 0.16, 4.6, 0, 0.68, 0);
  addBox(materials.olive, 1.88, 0.14, 4.55, 0, 0.8, 0);

  // Bonnet, grille, front panel and bumper.
  addBox(materials.olive, 1.86, 0.24, 1.02, 0, 1.04, -1.96);
  addBox(materials.oliveLight, 1.9, 0.1, 0.16, 0, 1.15, -2.43);
  addBox(materials.olive, 1.88, 0.3, 0.12, 0, 0.95, -2.46);
  addBox(materials.metal, 1.44, 0.18, 0.06, 0, 1.02, -2.5);
  addBox(materials.metal, 1.98, 0.14, 0.18, 0, 0.66, -2.53);
  for (const side of [-1, 1]) {
    addBox(materials.olive, 0.1, 0.3, 1.05, side * 0.93, 1.0, -1.96);
    addBox(materials.metal, 0.2, 0.12, 0.1, side * 0.5, 0.74, -2.6);
  }

  // The reference truck's windscreen is folded flat onto the bonnet.
  addBox(materials.olive, 1.88, 0.1, 0.26, 0, 1.2, -1.46);
  addBox(materials.metal, 1.64, 0.035, 0.8, 0, 1.2, -1.88, [-0.1, 0, 0]);
  addBox(materials.glass, 1.5, 0.03, 0.68, 0, 1.222, -1.88, [-0.1, 0, 0]);
  for (const side of [-1, 1]) {
    addBox(materials.oliveLight, 0.09, 0.05, 0.82, side * 0.79, 1.2, -1.88, [-0.1, 0, 0]);
  }
  addBox(materials.metal, 1.4, 0.05, 0.09, 0, 1.21, -1.5);

  // Open cab: doors, belt rails, rear bulkhead and a bare dashboard.
  for (const side of [-1, 1]) {
    addBox(materials.olive, 0.09, 0.5, 1.4, side * 0.93, 1.05, -0.78);
    addBox(materials.oliveLight, 0.1, 0.06, 1.4, side * 0.93, 1.33, -0.78);
    // Door cut lines, so the cab reads as a cab instead of one long slab.
    addBox(materials.oliveDark, 0.014, 0.44, 0.03, side * 0.938, 1.04, -1.42);
    addBox(materials.oliveDark, 0.014, 0.44, 0.03, side * 0.938, 1.04, -0.14);
    addBox(materials.oliveDark, 0.014, 0.03, 1.3, side * 0.938, 0.84, -0.78);
    addBox(materials.metal, 0.05, 0.05, 0.16, side * 0.97, 1.12, -0.5);
    // A-pillar stub framing the open cab.
    addBox(materials.olive, 0.11, 0.56, 0.11, side * 0.9, 1.06, -1.43);
  }
  addBox(materials.olive, 1.86, 0.5, 0.1, 0, 1.05, -0.09);
  addBox(materials.oliveLight, 1.86, 0.06, 0.12, 0, 1.33, -0.09);
  addBox(materials.oliveDark, 1.7, 0.03, 1.3, 0, 0.88, -0.78);
  addBox(materials.oliveDark, 1.7, 0.12, 0.28, 0, 1.22, -1.3);
  addCylinder(materials.metal, 0.16, 0.16, 0.035, 12, -0.42, 1.3, -1.1, [1.15, 0, 0]);
  addCylinder(materials.metal, 0.025, 0.025, 0.26, 6, -0.42, 1.24, -1.18, [0.95, 0, 0]);

  // Two seats; the crew sits on them with the cab sides cut away below the belt.
  for (const seatX of [-0.44, 0.46]) {
    addBox(materials.oliveDark, 0.5, 0.12, 0.46, seatX, 1.0, -0.72);
    addBox(materials.oliveDark, 0.5, 0.42, 0.1, seatX, 1.22, -0.93);
  }

  // Open bed: low sides, tailgate and rail caps.
  addBox(materials.olive, 1.82, 0.1, 2.5, 0, 0.92, 1.2);
  addBox(materials.olive, 0.1, 0.4, 2.5, -0.92, 1.14, 1.2);
  addBox(materials.olive, 0.1, 0.4, 2.5, 0.92, 1.14, 1.2);
  addBox(materials.olive, 1.86, 0.4, 0.1, 0, 1.14, 2.44);
  addBox(materials.oliveLight, 0.12, 0.05, 2.52, -0.92, 1.35, 1.2);
  addBox(materials.oliveLight, 0.12, 0.05, 2.52, 0.92, 1.35, 1.2);
  addBox(materials.oliveLight, 1.88, 0.05, 0.12, 0, 1.35, 2.44);
  addBox(materials.metal, 1.96, 0.13, 0.16, 0, 0.6, 2.55);

  // Spare wheel on the tailgate, blue barrel and stowed kit.
  addCylinder(materials.tire, 0.4, 0.4, 0.24, 14, 0, 1.2, 2.6, [Math.PI / 2, 0, 0]);
  addCylinder(materials.metal, 0.17, 0.17, 0.25, 10, 0, 1.2, 2.6, [Math.PI / 2, 0, 0]);
  addCylinder(materials.blue, 0.27, 0.27, 0.84, 12, 0.02, 1.4, 1.28);
  addCylinder(materials.metal, 0.282, 0.282, 0.045, 12, 0.02, 1.61, 1.28);
  addCylinder(materials.metal, 0.282, 0.282, 0.045, 12, 0.02, 1.19, 1.28);
  addBox(materials.tanDark, 0.36, 0.44, 0.18, -0.76, 1.22, 1.55);
  addBox(materials.oliveDark, 0.44, 0.24, 0.34, -0.24, 1.09, 2.12);
  addBox(materials.sand, 0.6, 0.16, 0.42, -0.5, 1.05, 1.0);

  // Pedestal-mounted heavy machine gun, pointing forward over the open cab.
  addCylinder(materials.metal, 0.075, 0.09, 0.52, 8, 0.22, 1.22, 0.34);
  addBox(materials.metal, 0.34, 0.1, 0.46, 0.22, 1.5, 0.3);
  addBox(materials.oliveDark, 0.24, 0.24, 0.74, 0.22, 1.64, 0.02);
  addCylinder(materials.metal, 0.048, 0.048, 1.05, 8, 0.22, 1.68, -0.82, [Math.PI / 2, 0, 0]);
  for (let i = 0; i < 3; i += 1) {
    addCylinder(materials.metal, 0.072, 0.072, 0.03, 8, 0.22, 1.68, -0.62 - i * 0.13, [Math.PI / 2, 0, 0]);
  }
  addBox(materials.metal, 0.11, 0.11, 0.17, 0.22, 1.68, -1.41);
  addBox(materials.oliveDark, 0.26, 0.22, 0.34, 0.5, 1.52, 0.16);
  for (const side of [-1, 1]) {
    addBox(materials.metal, 0.06, 0.06, 0.18, 0.22 + side * 0.09, 1.6, 0.46);
  }

  // Dusty camouflage blotches, thin panels proud of the bodywork.
  const patches = [
    [0.34, 1.165, -2.02, 0.52, 0.012, 0.44],
    [-0.42, 1.165, -1.7, 0.38, 0.012, 0.32],
    [0.12, 1.165, -1.56, 0.22, 0.012, 0.2],
    [-0.937, 1.1, -0.9, 0.012, 0.26, 0.42],
    [0.937, 1.18, -0.62, 0.012, 0.2, 0.34],
    [-0.927, 1.16, 0.7, 0.012, 0.24, 0.62],
    [0.927, 1.2, 1.5, 0.012, 0.2, 0.5],
    [-0.927, 1.2, 2.0, 0.012, 0.16, 0.3],
    [0.18, 1.165, -1.86, 0.24, 0.012, 0.2],
  ];
  for (const [x, y, z, width, height, depth] of patches) {
    addBox(materials.oliveDark, width, height, depth, x, y, z);
  }
  addBox(materials.sand, 0.9, 0.012, 0.3, -0.16, 1.166, -2.12);
  addBox(materials.sand, 0.012, 0.18, 0.34, 0.938, 1.1, -0.32);

  // Six-man crew: two in the cab, four in the bed. The gunner stands behind
  // the weapon; the others sit on the floor or brace against the bed sides.
  addSoldier(addBox, addCylinder, addSphere, materials, {
    x: -0.42,
    z: -0.7,
    hipY: 1.08,
    seated: true,
    armsForward: true,
  });
  addSoldier(addBox, addCylinder, addSphere, materials, {
    x: 0.46,
    z: -0.7,
    hipY: 1.06,
    seated: true,
  });
  addSoldier(addBox, addCylinder, addSphere, materials, {
    x: 0.2,
    z: 0.66,
    hipY: 1.46,
    armsForward: true,
  });
  addSoldier(addBox, addCylinder, addSphere, materials, {
    x: -0.56,
    z: 1.0,
    hipY: 1.44,
  });
  addSoldier(addBox, addCylinder, addSphere, materials, {
    x: -0.62,
    z: 1.8,
    hipY: 1.14,
    seated: true,
  });
  addSoldier(addBox, addCylinder, addSphere, materials, {
    x: 0.26,
    z: 1.9,
    hipY: 1.12,
    seated: true,
  });

  // Slender antenna and the lamps. Lamps are unlit so they read at night.
  addCylinder(materials.metal, 0.012, 0.022, 0.95, 4, -0.82, 1.98, 2.18);
  for (const side of [-1, 1]) {
    addBox(materials.headlight, 0.26, 0.14, 0.05, side * 0.62, 1.02, -2.56);
    addBox(materials.taillight, 0.24, 0.12, 0.05, side * 0.72, 1.1, 2.5);
  }

  const group = new Group();
  for (const [material, parts] of buckets) {
    const geometry = merge(parts);
    const mesh = new Mesh(geometry, material);
    const unlit = material === materials.headlight || material === materials.taillight;
    mesh.castShadow = !unlit;
    mesh.receiveShadow = !unlit;
    mesh.frustumCulled = false;
    group.add(mesh);
  }
  return group;
}

/**
 * One crew member. `hipY` is the top of the seat or the bed floor plus the
 * hip height, which keeps a seated man's torso above the cab belt line and a
 * standing man's chest above the bed rail.
 */
function addSoldier(addBox, addCylinder, addSphere, materials, {
  x,
  z,
  hipY,
  seated = false,
  armsForward = false,
}) {
  const torsoY = hipY + 0.26;
  addBox(materials.tan, 0.42, 0.52, 0.26, x, torsoY, z);
  addBox(materials.oliveDark, 0.44, 0.09, 0.28, x, hipY + 0.06, z);
  addBox(materials.tan, 0.46, 0.1, 0.26, x, hipY + 0.5, z);
  addCylinder(materials.skin, 0.068, 0.075, 0.12, 7, x, hipY + 0.58, z);
  addSphere(materials.skin, 0.135, x, hipY + 0.7, z);
  // Maroon beret: a squashed dome pulled down over the crown.
  addSphere(materials.maroon, 0.155, x, hipY + 0.765, z, [1, 0.62, 1]);
  addBox(materials.maroon, 0.19, 0.025, 0.08, x, hipY + 0.755, z - 0.12, [0, 0, 0.1]);

  if (armsForward) {
    for (const side of [-1, 1]) {
      addBox(materials.tan, 0.13, 0.13, 0.34, x + side * 0.2, hipY + 0.4, z - 0.2);
      addBox(materials.skin, 0.1, 0.1, 0.1, x + side * 0.16, hipY + 0.36, z - 0.4);
    }
  } else {
    for (const side of [-1, 1]) {
      addBox(materials.tan, 0.12, 0.4, 0.2, x + side * 0.275, hipY + 0.28, z);
      addBox(materials.skin, 0.1, 0.1, 0.13, x + side * 0.275, hipY + 0.06, z - 0.02);
    }
  }

  if (seated) {
    for (const side of [-1, 1]) {
      addBox(materials.tan, 0.16, 0.16, 0.4, x + side * 0.11, hipY - 0.04, z - 0.2);
      addBox(materials.tanDark, 0.16, 0.32, 0.16, x + side * 0.11, hipY - 0.22, z - 0.38);
      addBox(materials.metal, 0.17, 0.1, 0.26, x + side * 0.11, hipY - 0.4, z - 0.42);
    }
  } else {
    for (const side of [-1, 1]) {
      addBox(materials.tan, 0.15, 0.44, 0.18, x + side * 0.11, hipY - 0.24, z);
      addBox(materials.metal, 0.16, 0.11, 0.28, x + side * 0.11, hipY - 0.5, z - 0.03);
    }
  }
}

function createVisualUpdater(records, carMeshes) {
  const { byIndex, wheelMesh, headlights, taillights } = carMeshes;
  const touched = new Set();
  const carMatrix = new Matrix4();
  const partMatrix = new Matrix4();
  const zeroMatrix = new Matrix4().makeScale(0, 0, 0);
  const quaternion = new Quaternion();
  const wheelQuaternion = new Quaternion();
  const position = new Vector3();
  const local = new Vector3();
  const hazardPosition = new Vector3();
  const bodyScale = new Vector3();
  const partScale = new Vector3();

  const updateRecord = (record) => {
    const visual = record.visible === false ? 0 : (record.visualScale ?? 1);
    const ref = byIndex[record.index];
    if (!ref) return;

    touched.add(ref.bodyMesh);
    touched.add(ref.glassMesh);
    touched.add(ref.trimMesh);
    touched.add(wheelMesh);
    touched.add(headlights);
    touched.add(taillights);

    if (visual <= 0.001) {
      ref.bodyMesh.setMatrixAt(ref.groupIndex, zeroMatrix);
      ref.glassMesh.setMatrixAt(ref.groupIndex, zeroMatrix);
      ref.trimMesh.setMatrixAt(ref.groupIndex, zeroMatrix);
      for (let wheel = 0; wheel < 4; wheel += 1) {
        wheelMesh.setMatrixAt(record.index * 4 + wheel, zeroMatrix);
      }
      for (let lamp = 0; lamp < 2; lamp += 1) {
        headlights.setMatrixAt(record.index * 2 + lamp, zeroMatrix);
        taillights.setMatrixAt(record.index * 2 + lamp, zeroMatrix);
      }
      if (record.hazardMesh) {
        touched.add(record.hazardMesh);
        for (let corner = 0; corner < 4; corner += 1) {
          record.hazardMesh.setMatrixAt(record.hazardBase + corner, zeroMatrix);
        }
      }
      return;
    }

    quaternion.setFromAxisAngle(UP, record.yaw);
    position.set(record.x, record.y ?? 0.02, record.z);
    bodyScale.set(record.scale * visual, visual, record.scale * visual);
    carMatrix.compose(position, quaternion, bodyScale);
    ref.bodyMesh.setMatrixAt(ref.groupIndex, carMatrix);
    ref.glassMesh.setMatrixAt(ref.groupIndex, carMatrix);
    ref.trimMesh.setMatrixAt(ref.groupIndex, carMatrix);

    const geometry = ref.geometry;
    partScale.set(visual, visual, visual);
    for (let wheel = 0; wheel < 4; wheel += 1) {
      const x = wheel % 2 === 0 ? -0.82 : 0.82;
      const z = wheel < 2 ? -geometry.wheelZ : geometry.wheelZ;
      local.set(x, 0.34, z).applyMatrix4(carMatrix);
      wheelQuaternion.copy(quaternion);
      partMatrix.compose(local, wheelQuaternion, partScale);
      wheelMesh.setMatrixAt(record.index * 4 + wheel, partMatrix);
    }

    for (let lamp = 0; lamp < 2; lamp += 1) {
      const x = lamp === 0 ? -0.6 : 0.6;
      local.set(x, 0.73, -geometry.lightZ).applyMatrix4(carMatrix);
      partMatrix.compose(local, quaternion, partScale);
      headlights.setMatrixAt(record.index * 2 + lamp, partMatrix);

      local.set(x, 0.75, geometry.lightZ).applyMatrix4(carMatrix);
      partMatrix.compose(local, quaternion, partScale);
      taillights.setMatrixAt(record.index * 2 + lamp, partMatrix);
    }

    if (record.hazardMesh) {
      touched.add(record.hazardMesh);
      if (record.hazardOn) {
        for (let corner = 0; corner < 4; corner += 1) {
          const x = corner % 2 === 0 ? -0.79 : 0.79;
          const z = corner < 2 ? -record.hazardZ : record.hazardZ;
          hazardPosition.set(x, 0.66, z).applyMatrix4(carMatrix);
          partMatrix.compose(hazardPosition, quaternion, partScale);
          record.hazardMesh.setMatrixAt(record.hazardBase + corner, partMatrix);
        }
      } else {
        for (let corner = 0; corner < 4; corner += 1) {
          record.hazardMesh.setMatrixAt(record.hazardBase + corner, zeroMatrix);
        }
      }
    }
  };

  return {
    update() {
      for (const record of records) {
        const visual = record.visible === false ? 0 : (record.visualScale ?? 1);
        if (
          record._vx === record.x &&
          record._vz === record.z &&
          record._vyaw === record.yaw &&
          record._vscale === record.scale &&
          record._vvisual === visual &&
          record._vhazard === record.hazardOn
        ) {
          continue;
        }

        updateRecord(record);
        record._vx = record.x;
        record._vz = record.z;
        record._vyaw = record.yaw;
        record._vscale = record.scale;
        record._vvisual = visual;
        record._vhazard = record.hazardOn;
      }

      for (const mesh of touched) mesh.instanceMatrix.needsUpdate = true;
      touched.clear();
    },
  };
}

function createColliderUpdater(level, records) {
  const dimensions = new Map();
  const dirty = new Set();
  const position = new Vector3();
  const quaternion = new Quaternion();
  let timer = 0;

  for (const record of records) {
    const dims = carDimensions(record.type);
    dimensions.set(record, dims);
    quaternion.setFromAxisAngle(UP, record.yaw);
    const prop = new Prop({
      physics: level.world.physics,
      mesh: null,
      shape: { type: 'box', size: [dims.width, dims.height, dims.length] },
      mass: 0,
      position: [record.x, dims.height / 2, record.z],
      quaternion: quaternion.toArray(),
      friction: 0.9,
      restitution: 0,
      kinematic: true,
      group: GROUP.LEVEL,
      mask: MASK.LEVEL,
      userData: { type: 'traffic-car' },
    });
    level.register(prop);
    record.collider = prop.body;
    record.colliderHeight = dims.height / 2;
    record._cx = record.x;
    record._cz = record.z;
    record._cyaw = record.yaw;
    record._cvisual = record.visible === false ? 0 : 1;
  }

  const sync = (record) => {
    const dims = dimensions.get(record);
    const visible = record.visible !== false && (record.visualScale ?? 1) > 0.02;
    position.set(record.x, visible ? dims.height / 2 : -50, record.z);
    quaternion.setFromAxisAngle(UP, record.yaw);
    record.collider.teleport(position, quaternion);
  };

  return {
    update(dt) {
      for (const record of records) {
        const visual = record.visible === false ? 0 : 1;
        if (
          record._cx === record.x &&
          record._cz === record.z &&
          record._cyaw === record.yaw &&
          record._cvisual === visual
        ) {
          continue;
        }
        dirty.add(record);
      }

      timer += dt;
      if (timer < 0.05) return;
      timer = 0;

      for (const record of dirty) {
        sync(record);
        record._cx = record.x;
        record._cz = record.z;
        record._cyaw = record.yaw;
        record._cvisual = record.visible === false ? 0 : 1;
      }
      dirty.clear();
    },
  };
}

function getCarGeometry(type) {
  if (GEOMETRY_CACHE.has(type)) return GEOMETRY_CACHE.get(type);

  const geometry = type === 'pickup' ? buildPickup() : type === 'wagon' ? buildWagon() : buildSedan();
  GEOMETRY_CACHE.set(type, geometry);
  return geometry;
}

function buildSedan() {
  const body = merge([
    box(1.82, 0.46, 4.28, 0, 0.65, 0),
    box(1.76, 0.17, 1.28, 0, 0.9, -1.4),
    box(1.76, 0.15, 0.86, 0, 0.9, 1.65),
    box(1.44, 0.08, 1.52, 0, 1.46, 0.16),
  ]);
  const glass = cabinGlass({
    bottomY: 0.86,
    bottomWidth: 1.66,
    bottomDepth: 2.12,
    bottomZ: 0.1,
    topY: 1.42,
    topWidth: 1.4,
    topDepth: 1.48,
    topZ: 0.22,
  });
  const trim = merge([
    box(1.88, 0.16, 0.24, 0, 0.48, -2.16),
    box(1.88, 0.16, 0.24, 0, 0.48, 2.16),
    box(1.05, 0.2, 0.05, 0, 0.78, -2.2),
    box(0.16, 0.1, 0.22, -0.98, 1.02, -0.78),
    box(0.16, 0.1, 0.22, 0.98, 1.02, -0.78),
  ]);
  return { body, glass, trim, wheelZ: 1.4, lightZ: 2.16 };
}

function buildWagon() {
  const body = merge([
    box(1.84, 0.48, 4.42, 0, 0.66, 0),
    box(1.78, 0.17, 1.24, 0, 0.92, -1.55),
    box(1.46, 0.08, 2.9, 0, 1.5, 0.45),
  ]);
  const glass = cabinGlass({
    bottomY: 0.88,
    bottomWidth: 1.68,
    bottomDepth: 3.0,
    bottomZ: 0.42,
    topY: 1.46,
    topWidth: 1.42,
    topDepth: 2.52,
    topZ: 0.55,
  });
  const trim = merge([
    box(1.9, 0.16, 0.24, 0, 0.5, -2.2),
    box(1.9, 0.16, 0.24, 0, 0.5, 2.2),
    box(1.05, 0.2, 0.05, 0, 0.8, -2.24),
    box(0.16, 0.1, 0.22, -0.99, 1.05, -0.9),
    box(0.16, 0.1, 0.22, 0.99, 1.05, -0.9),
  ]);
  return { body, glass, trim, wheelZ: 1.45, lightZ: 2.2 };
}

function buildPickup() {
  const body = merge([
    box(1.82, 0.44, 4.5, 0, 0.66, 0),
    box(1.78, 0.18, 1.25, 0, 0.92, -1.55),
    box(1.52, 0.08, 1.3, 0, 1.5, -0.25),
    box(0.11, 0.42, 1.7, -0.86, 1.0, 1.28),
    box(0.11, 0.42, 1.7, 0.86, 1.0, 1.28),
    box(1.82, 0.42, 0.12, 0, 1.0, 2.17),
    box(1.72, 0.08, 1.55, 0, 0.9, 1.28),
  ]);
  const glass = cabinGlass({
    bottomY: 0.88,
    bottomWidth: 1.62,
    bottomDepth: 1.5,
    bottomZ: -0.28,
    topY: 1.46,
    topWidth: 1.4,
    topDepth: 1.0,
    topZ: -0.3,
  });
  const trim = merge([
    box(1.9, 0.16, 0.24, 0, 0.5, -2.23),
    box(1.9, 0.16, 0.24, 0, 0.5, 2.23),
    box(1.05, 0.2, 0.05, 0, 0.8, -2.27),
    box(1.55, 0.04, 1.55, 0, 0.93, 1.28),
    box(0.16, 0.1, 0.22, -0.99, 1.04, -0.76),
    box(0.16, 0.1, 0.22, 0.99, 1.04, -0.76),
  ]);
  return { body, glass, trim, wheelZ: 1.5, lightZ: 2.26 };
}

function box(width, height, depth, x, y, z) {
  const geometry = new BoxGeometry(width, height, depth);
  geometry.translate(x, y, z);
  return geometry;
}

function merge(parts) {
  const geometry = mergeGeometries(parts, false);
  for (const part of parts) part.dispose();
  return geometry;
}

function cabinGlass({
  bottomY,
  bottomWidth,
  bottomDepth,
  bottomZ,
  topY,
  topWidth,
  topDepth,
  topZ,
}) {
  const hwBottom = bottomWidth / 2;
  const hwTop = topWidth / 2;
  const hdBottom = bottomDepth / 2;
  const hdTop = topDepth / 2;
  const positions = [
    -hwBottom, bottomY, bottomZ - hdBottom,
    hwBottom, bottomY, bottomZ - hdBottom,
    hwBottom, bottomY, bottomZ + hdBottom,
    -hwBottom, bottomY, bottomZ + hdBottom,
    -hwTop, topY, topZ - hdTop,
    hwTop, topY, topZ - hdTop,
    hwTop, topY, topZ + hdTop,
    -hwTop, topY, topZ + hdTop,
  ];
  const indices = [
    0, 1, 5, 0, 5, 4,
    1, 2, 6, 1, 6, 5,
    2, 3, 7, 2, 7, 6,
    3, 0, 4, 3, 4, 7,
    4, 5, 6, 4, 6, 7,
  ];
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function addStationCones(level, stationSetups) {
  const coneMaterial = new MeshStandardMaterial({ color: 0xde5b2c, roughness: 0.8 });
  const stripeMaterial = new MeshStandardMaterial({ color: 0xf2e7d3, roughness: 0.8 });
  for (const station of stationSetups) {
    for (const localZ of [-29, 29]) {
      const point = station.frame.position
        .clone()
        .addScaledVector(station.frame.right, 14.9)
        .addScaledVector(station.frame.tangent, -localZ);
      for (let i = 0; i < 2; i += 1) {
        const offset = point.clone().addScaledVector(station.frame.tangent, -i * 1.0);
        const cone = new CylinderGeometry(0.02, 0.2, 0.52, 8);
        const mesh = new Mesh(cone, coneMaterial);
        mesh.position.set(offset.x, 0.28, offset.z);
        mesh.castShadow = true;
        level.root.add(mesh);
        const stripe = new Mesh(new CylinderGeometry(0.075, 0.125, 0.11, 8), stripeMaterial);
        stripe.position.set(offset.x, 0.34, offset.z);
        level.root.add(stripe);
      }
    }
  }
}
